import {RendererState} from '@cascade/core';
import clsx from 'clsx';
import {useCallback, useEffect, useMemo, useRef, useState, type CSSProperties} from 'react';
import {useApplication} from '../../contexts';
import {useRendererState, useSubscribableValue} from '../../hooks';
import {openOutputPath} from '../../utils';
import {buildExportComposition} from '../../saas/export';
import {Button, NumberInput, Select} from '../controls';
import tabsStyles from '../tabs/Tabs.module.scss';
import styles from './VideoSettings.module.scss';

type Orientation = 'landscape' | 'portrait' | 'square';

interface FormatPreset {
  value: [number, number];
  text: string;
}

const FORMAT_PRESETS: Record<Orientation, FormatPreset[]> = {
  landscape: [
    {value: [1920, 1080], text: '1080p - YouTube'},
    {value: [2560, 1440], text: '1440p - QHD'},
    {value: [3840, 2160], text: '4K UHD'},
    {value: [1280, 720], text: '720p'},
  ],
  portrait: [
    {value: [1080, 1920], text: 'Reels / TikTok (9:16)'},
    {value: [1080, 1350], text: 'Instagram Portrait (4:5)'},
  ],
  square: [{value: [1080, 1080], text: 'Instagram Post (1:1)'}],
};

const ORIENTATIONS: {key: Orientation; label: string}[] = [
  {key: 'landscape', label: 'Landscape'},
  {key: 'portrait', label: 'Portrait'},
  {key: 'square', label: 'Square'},
];

const FPS_OPTIONS = [24, 30, 60] as const;
const CODEC_OPTIONS = [
  {value: 'h264', label: 'H.264'},
  {value: 'h265', label: 'H.265'},
] as const;

function getOrientation(w: number, h: number): Orientation {
  if (w > h) return 'landscape';
  if (h > w) return 'portrait';
  return 'square';
}

function formatDuration(secs: number): string {
  if (secs < 60) return `${secs.toFixed(1)}s`;
  const m = Math.floor(secs / 60);
  const s = Math.round(secs % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function formatSize(mb: number): string {
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  if (mb >= 100) return `${Math.round(mb)} MB`;
  return `${mb.toFixed(1)} MB`;
}

/* ─── Custom Slider ─── */

function QualitySlider({
  value,
  min,
  max,
  onChange,
}: {
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const pct = ((value - min) / (max - min)) * 100;

  const updateFromEvent = useCallback(
    (clientX: number) => {
      const track = trackRef.current;
      if (!track) return;
      const rect = track.getBoundingClientRect();
      const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      onChange(Math.round(min + ratio * (max - min)));
    },
    [min, max, onChange],
  );

  useEffect(() => {
    if (!dragging) return;
    const handleMove = (e: MouseEvent) => updateFromEvent(e.clientX);
    const handleUp = () => setDragging(false);
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
    };
  }, [dragging, updateFromEvent]);

  return (
    <div className={styles.qualityControl}>
      <div
        ref={trackRef}
        className={styles.sliderTrack}
        onMouseDown={e => {
          updateFromEvent(e.clientX);
          setDragging(true);
        }}
      >
        <div className={styles.sliderFill} style={{width: `${pct}%`}} />
        <div
          className={clsx(
            styles.sliderThumb,
            dragging && styles.sliderThumbActive,
          )}
          style={{left: `${pct}%`}}
          onMouseDown={e => {
            e.stopPropagation();
            setDragging(true);
          }}
        />
      </div>
      <div className={styles.sliderLabels}>
        <span>Smaller file</span>
        <span>Higher quality</span>
      </div>
    </div>
  );
}

/* ─── Collapsible Encoding Section ─── */

function EncodingSection({
  fps,
  setFps,
  codec,
  setCodec,
  crf,
  sliderValue,
  onQualityChange,
}: {
  fps: number;
  setFps: (v: number) => void;
  codec: 'h264' | 'h265';
  setCodec: (v: 'h264' | 'h265') => void;
  crf: number;
  sliderValue: number;
  onQualityChange: (v: number) => void;
}) {
  const [open, setOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState(0);

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight);
    }
  }, [open, fps, codec, crf]);

  const codecLabel = CODEC_OPTIONS.find(c => c.value === codec)?.label ?? codec;
  const summary = `${fps} fps  ·  ${codecLabel}  ·  CRF ${crf}`;

  return (
    <div className={styles.section}>
      <button
        className={styles.sectionHeaderToggle}
        onClick={() => setOpen(o => !o)}
      >
        <svg className={styles.sectionIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="16 18 22 12 16 6" />
          <polyline points="8 6 2 12 8 18" />
        </svg>
        <span className={styles.sectionLabel}>Encoding</span>
        {!open && (
          <span className={styles.sectionSummary}>{summary}</span>
        )}
        <svg
          className={clsx(styles.chevron, open && styles.chevronOpen)}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      <div
        className={styles.collapsible}
        style={{height: open ? contentHeight : 0}}
      >
        <div ref={contentRef} className={styles.collapsibleInner}>
          {/* Frame rate */}
          <div className={styles.fieldRow}>
            <span className={styles.fieldLabel}>Frame rate</span>
          </div>
          <div className={styles.segmented}>
            {FPS_OPTIONS.map(f => (
              <button
                key={f}
                className={clsx(
                  styles.segmentedOption,
                  fps === f && styles.segmentedActive,
                )}
                onClick={() => setFps(f)}
              >
                {f} fps
              </button>
            ))}
          </div>

          {/* Codec */}
          <div className={styles.fieldRow}>
            <span className={styles.fieldLabel}>Codec</span>
          </div>
          <div className={styles.segmented}>
            {CODEC_OPTIONS.map(c => (
              <button
                key={c.value}
                className={clsx(
                  styles.segmentedOption,
                  codec === c.value && styles.segmentedActive,
                )}
                onClick={() => setCodec(c.value as 'h264' | 'h265')}
              >
                {c.label}
              </button>
            ))}
          </div>

          {/* Quality */}
          <div className={styles.qualityHeader}>
            <span className={styles.fieldLabel}>Quality</span>
            <span className={styles.qualityBadge}>CRF {crf}</span>
          </div>
          <QualitySlider
            value={sliderValue}
            min={1}
            max={51}
            onChange={onQualityChange}
          />
        </div>
      </div>
    </div>
  );
}

/* ─── Main Component ─── */

export function VideoSettings() {
  const {meta, renderer, project, player} = useApplication();
  const rendererState = useRendererState();

  // Size
  const sizeValue = useSubscribableValue(meta.shared.size.onChanged);
  const w = sizeValue?.x ?? 1920;
  const h = sizeValue?.y ?? 1080;

  // Derive orientation and format from current size
  const orientation = getOrientation(w, h);
  const presets = FORMAT_PRESETS[orientation];
  const presetIndex = presets.findIndex(
    p => p.value[0] === w && p.value[1] === h,
  );

  const formatOptions = [
    ...(presetIndex < 0 ? [{value: -1, text: 'Custom'}] : []),
    ...presets.map((p, i) => ({value: i, text: p.text})),
  ];

  const handleOrientationChange = (next: Orientation) => {
    if (next === orientation) return;
    const firstPreset = FORMAT_PRESETS[next][0];
    meta.shared.size.set(firstPreset.value);
  };

  const handleFormatChange = (idx: number) => {
    if (idx >= 0) {
      meta.shared.size.set(presets[idx].value);
    }
  };

  // Encoding state -- default fps from project scenes (usually 30)
  const projectFps = project.remotionScenes?.[0]?.fps ?? 30;
  const [fps, setFps] = useState(projectFps);
  const [codec, setCodec] = useState<'h264' | 'h265'>('h264');
  const [crf, setCrf] = useState(18);
  const [filename, setFilename] = useState('project');
  const isSaasMode = !renderer.canRenderServer;

  // Estimated stats
  const totalFrames = useMemo(() => {
    const scenes = project.remotionScenes ?? [];
    return scenes.reduce((sum, s) => sum + (s.durationInFrames ?? 600), 0);
  }, [project]);

  const durationSecs = totalFrames / fps;
  const estimatedSizeMB = useMemo(() => {
    const pixels = w * h;
    const bitsPerFrame = pixels * 0.07 * (30 / crf);
    const totalBits = bitsPerFrame * totalFrames;
    return totalBits / 8 / 1024 / 1024;
  }, [w, h, fps, crf, totalFrames]);

  const handleRender = useCallback(() => {
    if (renderer.canRenderServer) {
      // Dev mode: server-side render via WebSocket
      renderer.render({
        ...meta.getFullRenderingSettings(),
        name: 'cascade-combined',
        fps,
        codec,
        crf,
        outFile: `./output/${filename}.mp4`,
        audioClips: player.onAudioClipsChanged.current,
        audioTracks: player.onAudioTracksChanged.current,
      });
    } else {
      // SaaS mode: in-browser render via @remotion/web-renderer
      const scenes = project.remotionScenes ?? [];
      if (scenes.length === 0) return;

      const audioClips = player.onAudioClipsChanged.current;
      const audioTracks = player.onAudioTracksChanged.current;
      const component = buildExportComposition(scenes, fps, audioClips, audioTracks);

      renderer.renderInBrowser({
        component,
        durationInFrames: totalFrames,
        fps,
        width: w,
        height: h,
        filename,
      });
    }
  }, [meta, renderer, player, project, fps, codec, crf, filename, totalFrames, w, h]);

  const handleSnapshot = useCallback(() => {
    const time = player.status.framesToSeconds(player.onFrameChanged.current);
    renderer.renderFrame(
      {
        ...meta.getFullRenderingSettings(),
        name: 'cascade-combined',
        fps,
      },
      time,
    );
  }, [meta, renderer, player, fps]);

  const isRendering = rendererState !== RendererState.Initial;

  // Rich render progress
  const renderedFrames = useSubscribableValue(renderer.onFrameChanged);
  const renderTotalFrames = useSubscribableValue(renderer.onTotalFramesChanged);
  const renderStage = useSubscribableValue(renderer.onStageChanged);
  const renderCompletion = useSubscribableValue(renderer.estimator.onCompletionChanged);

  // CRF slider: invert so right = higher quality (lower CRF)
  const sliderValue = 52 - crf; // 1..51 range
  const handleQualityChange = useCallback(
    (v: number) => setCrf(52 - v),
    [],
  );

  return (
    <div className={clsx(styles.root, tabsStyles.pane)} id="settings-pane">
      {/* ─── Canvas Section ─── */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <svg className={styles.sectionIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <line x1="3" y1="9" x2="21" y2="9" />
          </svg>
          <span className={styles.sectionLabel}>Canvas</span>
        </div>

        {/* Orientation */}
        <div className={styles.segmented}>
          {ORIENTATIONS.map(o => (
            <button
              key={o.key}
              className={clsx(
                styles.segmentedOption,
                orientation === o.key && styles.segmentedActive,
              )}
              onClick={() => handleOrientationChange(o.key)}
            >
              {o.label}
            </button>
          ))}
        </div>

        {/* Format preset */}
        <div className={styles.controlWrap}>
          <Select
            options={formatOptions}
            value={presetIndex < 0 ? -1 : presetIndex}
            onChange={handleFormatChange}
          />
        </div>

        {/* Size */}
        <div className={clsx(styles.dimensionRow, styles.controlWrap)}>
          <NumberInput
            value={w}
            min={1}
            style={{flex: '1 1 0', minWidth: 0}}
            onChange={x => meta.shared.size.set([x, h])}
          />
          <span className={styles.dimensionSep}>&times;</span>
          <NumberInput
            value={h}
            min={1}
            style={{flex: '1 1 0', minWidth: 0}}
            onChange={y => meta.shared.size.set([w, y])}
          />
        </div>
      </div>

      {/* ─── Encoding Section (collapsible) ─── */}
      <EncodingSection
        fps={fps}
        setFps={setFps}
        codec={codec}
        setCodec={setCodec}
        crf={crf}
        sliderValue={sliderValue}
        onQualityChange={handleQualityChange}
      />

      {/* ─── Output Section ─── */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <svg className={styles.sectionIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          <span className={styles.sectionLabel}>Output</span>
        </div>

        {/* Filename */}
        <div className={styles.filenameRow}>
          <input
            className={styles.filenameInput}
            type="text"
            value={filename}
            onChange={e =>
              setFilename(e.target.value.replace(/[^a-zA-Z0-9_\-]/g, ''))
            }
            spellCheck={false}
          />
          <span className={styles.filenameSuffix}>.mp4</span>
        </div>

        {/* Stats bar */}
        <div className={styles.statsBar}>
          <div className={styles.stat}>
            <span className={styles.statValue}>
              {formatDuration(durationSecs)}
            </span>
            <span className={styles.statLabel}>Duration</span>
          </div>
          <div className={styles.statDivider} />
          <div className={styles.stat}>
            <span className={styles.statValue}>
              {totalFrames.toLocaleString()}
            </span>
            <span className={styles.statLabel}>Frames</span>
          </div>
          <div className={styles.statDivider} />
          <div className={styles.stat}>
            <span className={styles.statValue}>
              ~{formatSize(estimatedSizeMB)}
            </span>
            <span className={styles.statLabel}>Est. Size</span>
          </div>
        </div>
      </div>

      {/* ─── Render Progress ─── */}
      {isRendering && renderTotalFrames > 0 && (
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <span className={styles.sectionLabel}>
              {renderStage === 'audio-mixing' ? 'Mixing Audio...' :
               renderStage === 'stitching' ? 'Encoding...' : 'Rendering...'}
            </span>
          </div>
          <div className={styles.progressBar}>
            <div
              className={styles.progressFill}
              style={{width: `${Math.round(renderCompletion * 100)}%`}}
            />
          </div>
          <div className={styles.statsBar}>
            <div className={styles.stat}>
              <span className={styles.statValue}>
                {renderedFrames}/{renderTotalFrames}
              </span>
              <span className={styles.statLabel}>Frames</span>
            </div>
            <div className={styles.statDivider} />
            <div className={styles.stat}>
              <span className={styles.statValue}>
                {renderer.renderFps.toFixed(1)}
              </span>
              <span className={styles.statLabel}>FPS</span>
            </div>
            <div className={styles.statDivider} />
            <div className={styles.stat}>
              <span className={styles.statValue}>
                {Math.round(renderCompletion * 100)}%
              </span>
              <span className={styles.statLabel}>Progress</span>
            </div>
          </div>
        </div>
      )}

      {/* ─── Actions ─── */}
      <div className={styles.actions}>
        {!isRendering ? (
          <Button main className={styles.renderButton} onClick={handleRender}>
            <svg
              className={styles.buttonIcon}
              viewBox="0 0 24 24"
              fill="currentColor"
            >
              <path d="M8 5v14l11-7z" />
            </svg>
            Export Video
          </Button>
        ) : (
          <Button
            main
            loading
            className={styles.renderButton}
            disabled={rendererState === RendererState.Aborting}
            onClick={() => renderer.abort()}
          >
            {rendererState === RendererState.Working ? 'Abort' : 'Aborting'}
          </Button>
        )}

        {!isRendering && !isSaasMode && (
          <div className={styles.buttonRow}>
            <button
              className={styles.secondaryButton}
              onClick={handleSnapshot}
            >
              <svg
                className={styles.buttonIcon}
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <circle cx="12" cy="12" r="3.2" />
                <path d="M9 2 7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z" />
              </svg>
              Snapshot
            </button>
            <button
              className={styles.secondaryButton}
              onClick={openOutputPath}
            >
              <svg
                className={styles.buttonIcon}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" />
              </svg>
              Open Folder
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
